import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  History, 
  Download, 
  Upload, 
  Trash2, 
  Loader2,
  FileText,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentVersions = ({ documentId, documentName, onVersionUploaded }) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState('');

  const fetchVersions = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/documents/${documentId}/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersions(response.data.versions);
      setCurrentVersion(response.data.current_version);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [documentId, token]);

  useEffect(() => {
    if (open) {
      fetchVersions();
    }
  }, [open, fetchVersions]);

  const handleUploadVersion = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      let url = `${API}/documents/${documentId}/upload-version`;
      if (uploadNotes) {
        url += `?notes=${encodeURIComponent(uploadNotes)}`;
      }

      await axios.post(url, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('New version uploaded successfully');
      setUploadFile(null);
      setUploadNotes('');
      fetchVersions();
      if (onVersionUploaded) onVersionUploaded();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload new version');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadVersion = async (versionId, filename) => {
    try {
      const response = await axios.get(
        `${API}/documents/${documentId}/versions/${versionId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download version');
    }
  };

  const handleDeleteVersion = async (versionId, version) => {
    if (!confirm(`Are you sure you want to delete version ${version}?`)) return;
    
    try {
      await axios.delete(`${API}/documents/${documentId}/versions/${versionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Version ${version} deleted`);
      fetchVersions();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete version');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8"
          data-testid={`version-history-${documentId}`}
        >
          <History className="w-4 h-4 mr-1" />
          Versions
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Version History
          </DialogTitle>
          <DialogDescription>
            {documentName} - Current version: v{currentVersion}
          </DialogDescription>
        </DialogHeader>

        {/* Upload New Version */}
        <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
          <h4 className="font-medium flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Upload New Version
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Select File</Label>
              <Input 
                type="file" 
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="cursor-pointer"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input 
                placeholder="What changed in this version?"
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
              />
            </div>
          </div>
          <Button 
            onClick={handleUploadVersion} 
            disabled={uploading || !uploadFile}
            className="w-full md:w-auto"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Upload Version {currentVersion + 1}
          </Button>
        </div>

        {/* Version History Table */}
        <div className="border rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : versions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Clock className="w-8 h-8 mb-2 opacity-50" />
              <p>No version history available</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Filename</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map((version) => (
                  <TableRow key={version.version_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">v{version.version}</span>
                        {version.is_current && (
                          <Badge variant="default" className="text-xs">Current</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      {version.original_filename}
                    </TableCell>
                    <TableCell>{formatSize(version.size)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(version.created_at)}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate text-sm text-muted-foreground">
                      {version.notes || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDownloadVersion(version.version_id, version.original_filename)}
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {!version.is_current && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteVersion(version.version_id, version.version)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentVersions;
